import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { LoaderConfig } from 'src/app/components/widgets/loader/loader.config';
import { AlertInfo } from 'src/app/models/alert-info';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CustomerService } from 'src/app/services/api/customer.service';
import { LoaderService } from 'src/app/services/loader.service';
import { Subscription } from 'rxjs';

interface CustomerFile {
  name: string;
  url?: string;
  type?: string;
  size?: number;
  dateAdded?: Date;
}

interface UploadStatus {
  success: boolean;
  message: string;
}

@Component({
  selector: 'other-documents',
  imports: [CommonModule, ReactiveFormsModule, LoaderComponent],
  templateUrl: './other-documents.component.html',
  styleUrl: './other-documents.component.scss'
})

export class OtherDocumentsComponent implements OnInit, OnDestroy {
  @Input() customer: any;
  @Input() readonlyAttachmentsPanel!: boolean;

  files: any[] = [];
  selectedFile: File | null = null;
  fileDeleted: any;
  uploadStatus: UploadStatus | null = null;
  isLoading: boolean = false;
  alertInfo: AlertInfo = { show: false, type: '', message: '' };


  loaderConfig: LoaderConfig = {
    type: 'spinner',
    text: 'Progress...',
    overlay: true
  };
  private loaderSubscription: Subscription | null = null;
  private readonly LOADER_CONTEXT = 'other-documents';

  constructor(
    private alertNotificationService: AlertNotificationService,
    protected customerService: CustomerService,
    private loaderService: LoaderService,
    private confirmationDialogService: ConfirmationDialogService
  ) { }

  ngOnInit(): void {
    this.loaderSubscription = this.loaderService
      .getLoadingAsObservable(this.LOADER_CONTEXT)
      .subscribe(isLoading => this.isLoading = isLoading);

    setTimeout(() => {
      this.loadDocuments();
    }, 500);
  }

  ngOnDestroy(): void {
    if (this.loaderSubscription) {
      this.loaderSubscription.unsubscribe();
    }
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
  }

  loadDocuments(): void {
    this.files = [];
    if (this.customer && this.customer.customerId) {
      this.loaderService.startLoading(this.LOADER_CONTEXT);
      this.customerService.listOtheDocuments(this.customer.customerId).subscribe({
        next: (response) => {
          this.files = response.data.attachments;
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        },
        error: (error) => {
          console.error('Error loading attachments:', error);
          this.alertNotificationService.error('Error loading attachments');
          this.showAlertInfo('danger', 'Error loading attachments');
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        }
      });
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const selectedFile = input.files[0];
      this.uploadFile(selectedFile);
      input.value = '';
    }
  }

  viewFile(file: any): void {
    if (file && file.url) {
      this.customerService.GET(file.url).subscribe({
        next: (response) => {
          window.open(response.data.attachment.publicUrl, '_blank');
        }
      })
    } else {
      this.alertNotificationService.warning('File URL not available');
    }
  }

  confirmDelete(file: any) {
    const message = `Are you confident in your decision to delete this ${file.fileNameOriginal}?`;
    this.fileDeleted = file;
    
    this.confirmationDialogService.open(
      message,
      () => this.deleteFile(),
      () => { this.fileDeleted = null; }
    );
  }

  deleteFile(): void {
    if (this.fileDeleted && this.fileDeleted.attachmentId && this.customer) {
      this.loaderService.startLoading(this.LOADER_CONTEXT);
      this.customerService.deleteOtherAgreements(this.customer.customerId, this.fileDeleted.attachmentId).subscribe({
        next: (response) => {
          this.alertNotificationService.success('File deleted successfully');
          this.loadDocuments();
        },
        error: (error) => {
          console.error('Error deleting file:', error);
          this.alertNotificationService.error('Error deleting file');
          this.showAlertInfo('danger', 'Error deleting file');
          this.loaderService.stopLoading(this.LOADER_CONTEXT);
        },
        complete: () => {
          this.fileDeleted = null;
        }
      });
    } else {
      this.alertNotificationService.warning('Cannot delete file: missing file information');
    }
  }

  uploadFile(file: File): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);
    const formData = this.createFormDataForUpload(this.customer.customerId, file);

    this.customerService.createOtherDocuments(this.customer.customerId, formData).subscribe({
      next: (response) => {
        this.alertNotificationService.success(`File "${file.name}" uploaded successfully`);
        this.showAlertInfo('success', `File "${file.name}" uploaded successfully`);
        this.loadDocuments();
      },
      error: (error) => {
        console.error('Error uploading file:', error);
        this.alertNotificationService.error(`Error uploading file: ${error.error?.message || 'Unknown error'}`);
        this.showAlertInfo('danger', `Error uploading file: ${error.error?.message || 'Unknown error'}`);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
      }
    });
  }

  createFormDataForUpload(customerId: any, file: File): FormData {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('data', JSON.stringify(customerId));
    return formData;
  }

  showAlertInfo(type: string, message: string): void {
    this.alertInfo = {
      show: true,
      type,
      message
    };

    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
}
